<template>
  <div
    class="flex flex-col w-full h-full min-h-[420px] rounded-lg border border-[#d8d8e2] bg-[#f0f0f4] overflow-hidden"
    data-testid="document-viewer"
  >
    <div
      class="flex items-center justify-between gap-2 px-3 py-2 bg-white border-b border-[#e8e8ed] text-[12px] text-[#52526f]"
    >
      <span class="truncate font-semibold text-textPrimary">
        {{ versionId ? 'Arquivo enviado' : 'Sem arquivo' }}
      </span>
      <div v-if="url" class="flex items-center gap-1 shrink-0">
        <span class="mr-2">{{ metaText }}</span>
        <button
          v-for="control in CONTROLS"
          :key="control.testId"
          type="button"
          class="p-1 rounded hover:bg-gray-100"
          :aria-label="control.label"
          :data-testid="control.testId"
          @click="control.apply"
        >
          <v-icon :icon="control.icon" size="16" />
        </button>
      </div>
    </div>

    <div
      v-if="!versionId"
      class="flex-1 flex flex-col items-center justify-center gap-2 p-6 text-center"
      data-testid="document-viewer-empty"
    >
      <v-icon :icon="mdiFileHidden" size="40" class="text-[#bcbccd]" />
      <p class="text-sm font-semibold text-textPrimary">{{ emptyState.title }}</p>
      <p class="text-xs text-[#7f7f8e] max-w-xs">{{ emptyState.text }}</p>
    </div>
    <div
      v-else-if="isLoading"
      class="flex-1 flex items-center justify-center"
      data-testid="document-viewer-loading"
    >
      <v-progress-circular indeterminate color="primary" />
    </div>
    <div
      v-else-if="error"
      class="flex-1 flex items-center justify-center p-6 text-sm text-negative"
      role="alert"
      data-testid="document-viewer-error"
    >
      {{ error }}
    </div>
    <div v-else-if="url" class="relative flex-1 min-h-[360px]">
      <PdfRenderer
        v-model:page="page"
        :src="url"
        :scale="zoom"
        :rotation="rotation"
        :allow-open-in-new-tab="false"
      />
    </div>

    <p class="px-3 py-1.5 text-[11px] text-[#7f7f8e] bg-white border-t border-[#e8e8ed] text-left">
      Visualização com marca d'água de quem está vendo. Download desabilitado.
    </p>
  </div>
</template>

<script setup lang="ts">
import { computed, defineAsyncComponent, ref, toRef, watch } from 'vue'
import {
  mdiFileHidden,
  mdiMagnifyMinusOutline,
  mdiMagnifyPlusOutline,
  mdiRotateRight
} from '@mdi/js'

import { useDocumentFileUrl } from '@/composables/useDocumentFileUrl'

// Carga sob demanda: o `PdfRenderer` arrasta o `pdfjs-dist` (mesmo padrão do `StepPreview`).
const PdfRenderer = defineAsyncComponent(
  () => import('@/components/designSystem/modals/AttendanceUploadModal/PdfRenderer.vue')
)

const props = withDefaults(
  defineProps<{
    /** `null` = sem arquivo para mostrar (nunca enviado, legado ou sem acesso). */
    versionId: number | null
    isLegacy?: boolean
    /** Documento existe, mas não foi compartilhado com a empresa de quem vê (D80). */
    isRestricted?: boolean
    pageCount?: number | null
    sizeBytes?: number | null
  }>(),
  { isLegacy: false, isRestricted: false, pageCount: null, sizeBytes: null }
)

const { url, isLoading, error } = useDocumentFileUrl(toRef(props, 'versionId'))

const zoom = ref(1)
const rotation = ref(0)
const page = ref(1)

watch(
  () => props.versionId,
  () => {
    zoom.value = 1
    rotation.value = 0
    page.value = 1
  }
)

const CONTROLS = [
  {
    testId: 'document-viewer-zoom-out',
    label: 'Diminuir zoom',
    icon: mdiMagnifyMinusOutline,
    apply: () => (zoom.value = Math.max(0.6, +(zoom.value - 0.2).toFixed(1)))
  },
  {
    testId: 'document-viewer-zoom-in',
    label: 'Aumentar zoom',
    icon: mdiMagnifyPlusOutline,
    apply: () => (zoom.value = Math.min(2, +(zoom.value + 0.2).toFixed(1)))
  },
  {
    testId: 'document-viewer-rotate',
    label: 'Girar',
    icon: mdiRotateRight,
    apply: () => (rotation.value = (rotation.value + 90) % 360)
  }
]

const emptyState = computed(() => {
  if (props.isRestricted) {
    return {
      title: 'Arquivo não compartilhado com a sua empresa',
      text: 'O status do documento continua visível, mas o arquivo só aparece para quem o médico autorizou.'
    }
  }
  if (props.isLegacy) {
    return {
      title: 'Concluído (legado) — sem arquivo',
      text: 'Médico com vínculo anterior à data de corte: nunca bloqueia. O Drive convida a enviar.'
    }
  }
  return {
    title: 'O médico ainda não enviou este documento',
    text: 'Quando ele anexar pelo app, o documento entra direto em Em Análise.'
  }
})

const metaText = computed(() => {
  const parts = ['PDF']
  if (props.pageCount) {
    parts.push(`${props.pageCount} ${props.pageCount === 1 ? 'página' : 'páginas'}`)
  }
  if (props.sizeBytes) {
    parts.push(`${(props.sizeBytes / 1024 / 1024).toFixed(1).replace('.', ',')} MB`)
  }
  return parts.join(' · ')
})
</script>
